/**
 * Whether anything is stopping the strategy, and whether you would be told.
 *
 * Every other screen on this product answers "how is the account doing". This
 * one answers the question that went unanswered for the better part of a day:
 * new entries stood blocked, and the only symptom was that nothing opened.
 *
 * Three readings, each earned by a failure that happened and was noticed late.
 * Whether new exposure is being accepted and exactly which cycles are holding
 * it. What the notification queue has waiting and what never arrived. Whether
 * the venue reconciliation is current — it is the mechanism that clears the
 * gate above, so a gate that will not clear and a reconciliation that has
 * stopped are one fault seen from two ends.
 *
 * The verdict comes from the server. Deciding here when a queue is "behind"
 * would put the definition in the browser, where the next screen would define
 * it differently and nobody could say which one raised the alarm.
 *
 * It reads and cannot act. A console with buttons is a second control plane
 * beside the strategy, and two control planes over one account is how they
 * come to disagree.
 */
import { CircleAlert, CircleCheck } from "lucide-react";
import { Stat } from "@/design-system/Stat";

import { PageHeader } from "@/design-system/PageHeader";
import { Panel } from "@/design-system/Panel";
import { ErrorState, Forbidden, Loading } from "@/design-system/states/States";
import type { ExposureBlockerResponse, SystemResponse } from "@/shared/api";
import { humanAge } from "@/shared/time/age";
import { plural } from "@/shared/text/plural";
import { findingText, reasonText } from "@/shared/text/reasons";

import { useSystem } from "./useSystem";
import { ProductionRelease } from "./ProductionRelease";
import { ReleaseConformance } from "./ReleaseConformance";

const READINESS: Record<string, string> = {
  READY: "Принимаются",
  SLOT_BLOCKED: "Закрыты для части слотов",
  ACCOUNT_BLOCKED: "Закрыты для всего счёта",
};

function moment(at: string): string {
  return new Date(at).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** How long ago, from a stamp, without the browser deciding what "now" means
 *  for anything but this label. */
function ago(at: string, asOf: string): string {
  const seconds = (new Date(asOf).getTime() - new Date(at).getTime()) / 1000;
  return seconds < 0 ? "только что" : `${humanAge(seconds)} назад`;
}

/** A machine reading, in the shared window, warning-toned when it is one. */
function Fact({
  label,
  value,
  note,
  testId,
  tone = "plain",
}: {
  readonly label: string;
  readonly value: string;
  readonly note?: string | undefined;
  readonly testId: string;
  readonly tone?: "plain" | "warning";
}): React.JSX.Element {
  return (
    <Stat
      label={label}
      note={note}
      value={
        // The mark is on the figure, not on the window around it: a reading
        // that is a warning is the number, and the tests read the number.
        <span
          data-testid={testId}
          className={tone === "warning" ? "text-status-warning" : undefined}
        >
          {value}
        </span>
      }
    />
  );
}

function Blockers({
  blockers,
  asOf,
}: {
  readonly blockers: readonly ExposureBlockerResponse[];
  readonly asOf: string;
}): React.JSX.Element {
  return (
    <ul className="divide-y divide-border-subtle" data-testid="exposure-blockers">
      {blockers.map((blocker) => (
        <li
          key={blocker.cycle_id}
          data-testid={`blocker-${blocker.slot_id}`}
          className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2"
        >
          <span className="min-w-0">
            <span className="block text-body-main text-text-primary">
              {blocker.symbol}
              <span className="ml-2 font-mono text-helper-sm text-text-muted">
                {blocker.slot_id}
              </span>
            </span>
            {/* The word is for reading; the ledger's code stays on hover, so
                the journal and the database can still be searched for it. */}
            <span className="block text-helper-sm text-text-muted" title={blocker.reason ?? undefined}>
              {blocker.reason === null ? "причина не записана" : reasonText(blocker.reason)}
            </span>
          </span>
          <span className="shrink-0 text-helper-sm tabular-nums text-text-muted">
            {blocker.since === null ? "—" : ago(blocker.since, asOf)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Readings({ system }: { readonly system: SystemResponse }): React.JSX.Element {
  const { exposure, outbox, reconciliation } = system;
  const blocked = exposure.readiness !== "READY";

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {/* The verdict first, and it is one line when there is nothing wrong.
          A screen that lists eight healthy readings teaches the reader to skip
          the place the unhealthy one will appear. */}
      <section
        data-testid="system-verdict"
        data-healthy={system.findings.length === 0 ? "true" : "false"}
        className={`flex min-w-0 items-start gap-2 rounded-panel border px-3 py-2 ${
          system.findings.length === 0
            ? "border-border-subtle bg-surface-default"
            : "border-status-warning bg-surface-default"
        }`}
      >
        {system.findings.length === 0 ? (
          <CircleCheck aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-status-positive" />
        ) : (
          <CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-status-warning" />
        )}
        <div className="min-w-0">
          <p className="text-label-caps text-text-secondary">
            {system.findings.length === 0
              ? "Ничего не требует внимания"
              : `Требуют внимания: ${String(system.findings.length)}`}
          </p>
          {system.findings.length > 0 && (
            <ul className="mt-1 flex flex-col gap-0.5">
              {system.findings.map((finding) => (
                <li
                  key={finding}
                  data-testid={`finding-${finding}`}
                  className="text-helper-sm text-text-secondary"
                >
                  {findingText(finding)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <div className="grid min-w-0 grid-cols-1 items-start gap-3 xl:grid-cols-12">
        <div className="min-w-0 xl:col-span-7">
          <Panel title="Приём новых входов" testId="system-exposure">
            <div className="flex flex-col gap-2">
              <Fact
                label="Состояние"
                testId="exposure-readiness"
                value={READINESS[exposure.readiness] ?? exposure.readiness}
                tone={blocked ? "warning" : "plain"}
                note={
                  exposure.account_blocked_since === null
                    ? undefined
                    : `закрыт с ${moment(exposure.account_blocked_since)}`
                }
              />
              {exposure.blockers.length === 0 ? (
                <p className="text-helper-sm text-text-muted" data-testid="exposure-clear">
                  Ни один цикл не удерживает вход.
                </p>
              ) : (
                <>
                  <p className="text-label-caps text-text-muted">
                    {`${String(exposure.blockers.length)} ${plural(
                      exposure.blockers.length,
                      "цикл удерживает",
                      "цикла удерживают",
                      "циклов удерживают",
                    )} вход`}
                  </p>
                  <Blockers blockers={exposure.blockers} asOf={system.as_of} />
                </>
              )}
            </div>
          </Panel>
        </div>

        <div className="flex min-w-0 flex-col gap-3 xl:col-span-5">
          <Panel title="Сверка с биржей" testId="system-reconciliation">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <Fact
                label="Подтверждено до"
                testId="reconciliation-through"
                value={
                  reconciliation.reconciled_through === null
                    ? "—"
                    : ago(reconciliation.reconciled_through, system.as_of)
                }
                note={
                  reconciliation.reconciled_through === null
                    ? "сверка ни разу не проходила"
                    : moment(reconciliation.reconciled_through)
                }
              />
              <Fact
                label="Циклов под сверкой"
                testId="reconciliation-tracked"
                value={String(reconciliation.cycles_tracked)}
              />
              <Fact
                label="Проблемы сверки"
                testId="reconciliation-incomplete"
                value={String(reconciliation.incomplete)}
                tone={reconciliation.incomplete > 0 ? "warning" : "plain"}
                note={
                  reconciliation.incomplete === 0
                    ? reconciliation.updating > 0
                      ? `обновляется сейчас: ${String(reconciliation.updating)}`
                      : undefined
                    : `расхождение: ${String(reconciliation.divergent.length)} · прервано: ${String(reconciliation.interrupted.length)} · устарело: ${String(reconciliation.stale.length)}`
                }
              />
            </div>
          </Panel>

          <Panel title="Очередь уведомлений" testId="system-outbox">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <Fact
                label="Ожидают отправки"
                testId="outbox-pending"
                value={String(outbox.pending)}
                note={
                  outbox.oldest_pending_at === null
                    ? undefined
                    : `самое старое ${ago(outbox.oldest_pending_at, system.as_of)}`
                }
              />
              <Fact
                label="Доставлено за сутки"
                testId="outbox-delivered"
                value={String(outbox.delivered_last_day)}
              />
              <Fact
                label="Не доставлено"
                testId="outbox-dead"
                value={String(outbox.dead)}
                tone={outbox.dead > 0 ? "warning" : "plain"}
                note={outbox.dead === 0 ? undefined : "исчерпаны попытки"}
              />
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

export function SystemPage(): React.JSX.Element {
  const state = useSystem();

  return (
    <div className="flex min-w-0 flex-col gap-2" data-testid="system-page">
      <PageHeader
        title="Система"
        description="Принимает ли система новые входы, сходится ли учёт с биржей и что стоит в очереди уведомлений."
      />
      {state.status === "loading" ? (
        <Loading title="Читаю состояние системы…" />
      ) : state.status === "forbidden" ? (
        <Forbidden />
      ) : state.status === "unavailable" ? (
        // Not an all-clear. A deployment composed without the reader knows
        // nothing about the machine, and saying nothing would read as
        // "nothing is wrong" — the one answer it cannot know to be true.
        <ErrorState
          title="Операторские показания не настроены"
          detail="Эта сборка не получила доступ к ним. Позиции и ордера на бирже от этого экрана не зависят."
        />
      ) : state.status === "unreadable" ? (
        <ErrorState
          title="Не могу прочитать состояние системы"
          detail="Показания не пришли. Позиции и ордера на бирже от этого экрана не зависят."
        />
      ) : (
        <Readings system={state.system} />
      )}
      <ProductionRelease />
      <ReleaseConformance />
    </div>
  );
}
